import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TableColumnConfig } from '@core/models/skeleton.model';

/**
 * A single "global setting" row rendered above a `xemex-table`, e.g. to toggle a value for every
 * row at once. It reads its grid track widths directly from the same `columns` config passed to
 * the table below it, so the two layouts can never drift out of alignment — unlike a hand-built
 * flex row whose widths/paddings have to be kept in sync manually.
 */
@Component({
  selector: 'xemex-table-global-row',
  standalone: true,
  imports: [MatSlideToggleModule],
  templateUrl: './table-global-row.component.html',
  styleUrl: './table-global-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableGlobalRowComponent {
  /** Same array passed to the `xemex-table` below this row. */
  columns = input.required<TableColumnConfig[]>();
  label = input.required<string>();
  /** Checked state per toggle-type column, keyed by `field`. */
  values = input.required<Record<string, boolean>>();
  /** Width of the table's trailing actions column, if it has one (e.g. `'3rem'`). Omit if none. */
  actionsWidth = input<string | undefined>(undefined);

  toggleChange = output<{ field: string }>();

  protected readonly toggleColumns = computed(() =>
    this.columns().filter(col => col.type === 'toggle'),
  );

  /** Label spans every leading column up to (excluding) the first toggle column. */
  protected readonly labelSpan = computed(() => {
    const cols = this.columns();
    const firstToggleIndex = cols.findIndex(col => col.type === 'toggle');
    return firstToggleIndex === -1 ? cols.length : firstToggleIndex;
  });

  protected readonly gridTemplateColumns = computed(() => {
    const widths = this.columns().map(col => col.width ?? 'auto');
    const actionsWidth = this.actionsWidth();
    return actionsWidth ? [...widths, actionsWidth].join(' ') : widths.join(' ');
  });

  protected onToggle(field: string): void {
    this.toggleChange.emit({ field });
  }
}
